/**
 * UI-facing DTOs for the local data source (Phase 3).
 *
 * These functions render local rows into EXACTLY the shapes the existing
 * React POS consumes from the API today (`serialiseOrder` and
 * `getInvoiceById` on the server) - rupees as numbers, same field names,
 * same derived values - so the UI cannot tell the two data sources apart.
 * Money leaves paise only here, via toRupees, at the very edge.
 *
 * Kept in this package (not apps/web) so the shapes are covered by the
 * Node test suite (tests/dto.test.ts).
 */
import { applyOffers, toRupees, totalise } from '@kortex/shared';
import { LocalDataError } from './types.js';
import { computeCharges, computeSettle } from './domain/totals.js';
import { rateFromBp, qtyFromMilli } from './domain/totals.js';
import { linesForOffers, tillContext } from './domain/till.js';
// ---------------------------------------------------------------------------
export async function listMenuDtos(db) {
    const [items, categories] = await Promise.all([
        db.catalog.searchMenuItems('', { activeOnly: true, limit: 10_000 }),
        db.catalog.listCategories(),
    ]);
    const categoryName = new Map(categories.map((c) => [c.id, c.name]));
    return items.map((m) => ({
        id: m.id,
        sku: m.sku,
        name: m.name,
        categoryId: m.categoryId,
        category: categoryName.get(m.categoryId) ?? '',
        price: toRupees(m.pricePaise),
        gstRate: rateFromBp(m.gstRateBp),
        hsn: m.hsn,
        station: m.station,
        isVegetarian: m.isVegetarian,
        discountPct: rateFromBp(m.discountPctBp),
        foodCostPct: rateFromBp(m.foodCostPctBp),
        isActive: m.isActive,
    }));
}
/** The exact `serialiseOrder` composition, over local rows. */
async function ticketDtoFromOrder(db, order) {
    const till = await tillContext(db.driver, db.offers);
    const offerLines = await linesForOffers(db.driver, order.lines);
    const offersOutcome = order.lines.length
        ? applyOffers({ lines: offerLines, offers: till.offers, at: new Date(), timeZone: till.timeZone })
        : { applied: [], perLine: [], total: 0 };
    // Totals exactly as the server serialises an open ticket: item discounts
    // and running offers applied, no bill discount yet, intra-state - the
    // shared engine either way.
    const { taxed, itemDiscount, offerDiscount } = order.lines.length
        ? computeSettle(order.lines.map((l) => ({
            unitPricePaise: l.unitPricePaise,
            quantityMilli: l.quantityMilli,
            discountPctBp: l.discountPctBp,
            gstRateBp: l.gstRateBp,
        })), 0, false, offersOutcome.perLine)
        : { taxed: [], itemDiscount: 0, offerDiscount: 0 };
    const chargeLines = order.lines.length
        ? computeCharges(taxed, { orderType: order.type, settings: till.charges, interState: false })
        : [];
    const totals = totalise([...taxed, ...chargeLines.map((c) => c.taxed)]);
    const tableCode = order.tableId
        ? ((await db.tables.getById(order.tableId))?.code ?? null)
        : null;
    const stay = order.stayId ? await db.stays.getById(order.stayId) : null;
    return {
        id: order.id,
        number: order.number,
        type: order.type,
        status: order.status,
        covers: order.covers,
        note: order.note,
        openedAt: order.openedAt,
        closedAt: order.closedAt,
        tableId: order.tableId,
        tableCode,
        stayId: order.stayId,
        roomNumber: stay?.roomNumber ?? null,
        guestName: stay?.guestName ?? null,
        itemCount: order.lines.reduce((sum, l) => sum + qtyFromMilli(l.quantityMilli), 0),
        lines: order.lines.map((l, i) => ({
            id: l.id,
            menuItemId: l.menuItemId,
            name: l.name,
            station: l.station,
            quantity: qtyFromMilli(l.quantityMilli),
            unitPrice: toRupees(l.unitPricePaise),
            discountPct: rateFromBp(l.discountPctBp),
            discount: toRupees(taxed[i]?.discount ?? 0),
            gstRate: rateFromBp(l.gstRateBp),
            amount: toRupees(taxed[i]?.gross ?? 0),
            note: l.note,
            kdsState: l.kdsState,
            firedAt: l.firedAt,
        })),
        offers: offersOutcome.applied.map((a) => ({
            id: a.offerId,
            name: a.name,
            type: a.type,
            times: a.times,
            discount: toRupees(a.discount),
        })),
        charges: chargeLines.map(({ charge, taxed: t }) => ({
            key: charge.key,
            label: charge.label,
            amount: toRupees(charge.amount),
            gstRate: t.gstRate,
        })),
        totals: {
            subtotal: toRupees(totals.subtotal),
            itemDiscount: toRupees(itemDiscount),
            offerDiscount: toRupees(offerDiscount),
            charges: toRupees(chargeLines.reduce((sum, c) => sum + c.charge.amount, 0)),
            cgst: toRupees(totals.cgst),
            sgst: toRupees(totals.sgst),
            igst: toRupees(totals.igst),
            tax: toRupees(totals.tax),
            roundOff: toRupees(totals.roundOff),
            total: toRupees(totals.total),
        },
    };
}
export async function getTicketDto(db, orderId) {
    const order = await db.orders.getOrder(orderId);
    if (!order)
        throw new LocalDataError('ORDER_NOT_FOUND', 'That ticket does not exist');
    return ticketDtoFromOrder(db, order);
}
export async function listTicketDtos(db, status = 'OPEN') {
    const rows = await db.orders.listOrders(status);
    const out = [];
    for (const row of rows) {
        const order = await db.orders.getOrder(row.id);
        if (order)
            out.push(await ticketDtoFromOrder(db, order));
    }
    return out;
}
export async function getInvoiceDto(db, invoiceId) {
    const invoice = await db.invoices.getInvoice(invoiceId);
    if (!invoice)
        throw new LocalDataError('INVOICE_NOT_FOUND', 'That invoice does not exist');
    const propertyRows = await db.driver.query({ sql: 'SELECT * FROM property LIMIT 1' });
    const property = propertyRows[0];
    if (!property) {
        throw new LocalDataError('PROPERTY_NOT_PROVISIONED', 'Property profile missing on this device');
    }
    const order = invoice.orderId ? await db.orders.getOrder(invoice.orderId) : null;
    const tableCode = order?.tableId ? ((await db.tables.getById(order.tableId))?.code ?? null) : null;
    const stay = order?.stayId ? await db.stays.getById(order.stayId) : null;
    // Slab summary rebuilt from the stored lines (same grouping the server's
    // slabSummary performs over TaxedLines).
    const slabMap = new Map();
    for (const line of invoice.lines) {
        const rate = rateFromBp(line.gstRateBp);
        const row = slabMap.get(rate) ?? { gstRate: rate, taxable: 0, cgst: 0, sgst: 0, igst: 0 };
        row.taxable += line.taxablePaise;
        row.cgst += line.cgstPaise;
        row.sgst += line.sgstPaise;
        row.igst += line.igstPaise;
        slabMap.set(rate, row);
    }
    const slabs = [...slabMap.values()]
        .sort((a, b) => a.gstRate - b.gstRate)
        .map((s) => ({
        gstRate: s.gstRate,
        taxable: toRupees(s.taxable),
        cgst: toRupees(s.cgst),
        sgst: toRupees(s.sgst),
        igst: toRupees(s.igst),
    }));
    return {
        id: invoice.id,
        number: invoice.number,
        kind: invoice.kind,
        status: invoice.status,
        guestName: invoice.guestName,
        guestGstin: invoice.guestGstin,
        placeOfSupply: invoice.placeOfSupply,
        isInterState: invoice.isInterState,
        mode: invoice.mode,
        issuedAt: invoice.issuedAt,
        ticketNumber: order?.number ?? null,
        orderType: order?.type ?? null,
        tableCode,
        roomNumber: stay?.roomNumber ?? null,
        subtotal: toRupees(invoice.subtotalPaise),
        discount: toRupees(invoice.discountPaise),
        offers: parseOffersJson(invoice.offersJson),
        cgst: toRupees(invoice.cgstPaise),
        sgst: toRupees(invoice.sgstPaise),
        igst: toRupees(invoice.igstPaise),
        roundOff: toRupees(invoice.roundOffPaise),
        tax: toRupees(invoice.cgstPaise + invoice.sgstPaise + invoice.igstPaise),
        total: toRupees(invoice.totalPaise),
        property: {
            name: String(property.name),
            address: property.address == null ? '' : String(property.address),
            city: property.city == null ? '' : String(property.city),
            pincode: property.pincode == null ? null : String(property.pincode),
            gstin: String(property.gstin),
            phone: property.phone == null ? '' : String(property.phone),
            fssai: property.fssai == null ? null : String(property.fssai),
            stateCode: String(property.state_code),
        },
        lines: invoice.lines.map((l) => ({
            id: l.id,
            description: l.description,
            hsn: l.hsn,
            quantity: qtyFromMilli(l.quantityMilli),
            unitPrice: toRupees(l.unitPricePaise),
            gstRate: rateFromBp(l.gstRateBp),
            discount: toRupees(l.discountPaise),
            taxable: toRupees(l.taxablePaise),
            cgst: toRupees(l.cgstPaise),
            sgst: toRupees(l.sgstPaise),
            igst: toRupees(l.igstPaise),
            total: toRupees(l.totalPaise),
        })),
        slabs,
    };
}
export async function listInvoiceDtos(db, options = {}) {
    // A day range is applied below, after the read, so the read has to reach back far
    // enough to include it: asking for the latest 100 and then keeping only last
    // Tuesday would return nothing on a busy till.
    const dated = Boolean(options.from || options.to);
    const rows = await db.invoices.listInvoices({ limit: dated ? 2000 : (options.limit ?? 100) });
    const day = (iso) => {
        const at = new Date(iso);
        return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
    };
    const term = options.q?.trim().toLowerCase();
    const out = [];
    for (const invoice of rows) {
        if (options.kind && invoice.kind !== options.kind)
            continue;
        if (options.from && day(invoice.issuedAt) < options.from)
            continue;
        if (options.to && day(invoice.issuedAt) > options.to)
            continue;
        if (term &&
            !invoice.number.toLowerCase().includes(term) &&
            !invoice.guestName.toLowerCase().includes(term)) {
            continue;
        }
        const order = invoice.orderId ? await db.orders.getOrder(invoice.orderId) : null;
        out.push({
            id: invoice.id,
            number: invoice.number,
            kind: invoice.kind,
            status: invoice.status,
            guestName: invoice.guestName,
            ticketNumber: order?.number ?? null,
            orderType: order?.type ?? null,
            subtotal: toRupees(invoice.subtotalPaise),
            tax: toRupees(invoice.cgstPaise + invoice.sgstPaise + invoice.igstPaise),
            total: toRupees(invoice.totalPaise),
            mode: invoice.mode,
            issuedAt: invoice.issuedAt,
        });
    }
    return out;
}
export async function listTableDtos(db) {
    const rows = await db.tables.listWithOccupancy();
    return rows.map((t) => ({
        id: t.id,
        code: t.code,
        seats: t.seats,
        section: t.section,
        isOccupied: t.isOccupied,
    }));
}
export async function listStayDtos(db) {
    const rows = await db.stays.listInHouse();
    return rows.map((s) => ({
        id: s.id,
        roomNumber: s.roomNumber ?? '',
        guestName: s.guestName,
    }));
}
function parseOffersJson(raw) {
    if (raw === null)
        return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return undefined;
        return parsed;
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=dto.js.map